/**
 * Thin typed wrapper around the global `window.aleaTelemetry` object that is
 * initialised by the inline bootstrap snippet + telemetry.js (loaded from
 * public/index.html). All functions are best-effort — telemetry must never
 * break the assessment, so every call swallows its own errors.
 */

export type TelemetryCategory =
  | 'RuntimeInit'
  | 'SnapshotRestore'
  | 'TaskListDownload'
  | 'ItemConfigLoad'
  | 'TaskSwitch'
  | 'TraceUpload';

interface AleaTelemetry {
  bootStart: number;
  recordEvent: (category: string, name: string, opts?: Record<string, unknown>) => void;
  flush?: () => void;
}

function getTelemetry(): AleaTelemetry | undefined {
  const t = (window as any).aleaTelemetry;
  return t && typeof t.recordEvent === 'function' ? t : undefined;
}

export interface TelemetryOpts {
  /** performance.now() when the measured operation started. Defaults to bootStart. */
  startMs?: number;
  /** performance.now() when the operation ended. Defaults to now(). */
  endMs?: number;
  /** false if the operation failed (timeout/error). Defaults to true. */
  success?: boolean;
  runId?: string;
  assessmentSessionId?: string;
  itemId?: string;
  /** Free-form string attributes (e.g. sub-timings, error messages, counts). */
  attributes?: Record<string, string>;
}

/** High-resolution timestamp for measuring spans. */
export function telemetryNow(): number {
  return performance.now();
}

/** Records a telemetry event. Best-effort: swallows all errors. */
export function recordTelemetry(category: TelemetryCategory, name: string, opts: TelemetryOpts = {}): void {
  try {
    const t = getTelemetry();
    if (!t) return;
    t.recordEvent(category, name, {
      startTime: opts.startMs,
      endTime: opts.endMs,
      success: opts.success,
      runId: opts.runId,
      assessmentSessionId: opts.assessmentSessionId,
      itemId: opts.itemId,
      attributes: opts.attributes || {},
    });
  } catch {
    /* best-effort: telemetry failures must not affect the assessment */
  }
}

/** Flushes the telemetry queue. Best-effort. */
export function flushTelemetry(): void {
  try {
    getTelemetry()?.flush?.();
  } catch {
    /* best-effort */
  }
}